import React, { useState, useMemo } from 'react';
import { useProducts } from '../context/ProductContext';
import { useQuote } from '../context/QuoteContext';
import { useSiteImages } from '../context/SiteImagesContext';
import { SafeImage } from '../components/SafeImage';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { formatKsh } from '../utils/formatters';
import { Search, ShoppingBag, Eye, Check, ExternalLink, ArrowRight } from 'lucide-react';

interface ProductsPageProps {
  onNavigate: (page: string, productId?: string) => void;
}

export const ProductsPage: React.FC<ProductsPageProps> = ({ onNavigate }) => {
  const { products } = useProducts();
  const { addToQuote, items } = useQuote();
  const { images } = useSiteImages();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'featured' | 'price-asc' | 'price-desc'>('featured');

  const categories = [
    { id: 'all', label: 'All Equipment' },
    { id: 'water-purification', label: 'Water Purification (RO)' },
    { id: 'milk-atm', label: 'Milk ATMs' },
    { id: 'water-vending', label: 'Water Vending Stations' },
    { id: 'cooking-oil', label: 'Cooking Oil ATMs' },
    { id: 'pasteurizers', label: 'Milk Pasteurizers' },
    { id: 'spares', label: 'Spares & Servicing' }
  ];

  const filteredProducts = useMemo(() => {
    return products.filter((item) => {
      const matchCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.capacity.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCategory && matchSearch;
    }).sort((a, b) => {
      if (sortBy === 'price-asc') return a.priceKsh - b.priceKsh;
      if (sortBy === 'price-desc') return b.priceKsh - a.priceKsh;
      return 0;
    });
  }, [products, selectedCategory, searchQuery, sortBy]);

  const isInQuote = (productId: string) => items.some((i) => i.product.id === productId);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="Products"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<ShoppingBag className="w-3.5 h-3.5" />}
        badgeText="Factory Showroom & Industrial Equipment Catalog"
        badgeColor="emerald"
        title="Commercial Machinery & Purification Plants"
        description="Engineered and calibrated at Juja Road Green Mall, Nairobi. Built with food-grade SS 304/316, GSM telemetry controllers, and comprehensive warranty support across Kenya."
        backgroundImage={images.productsBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">

      {/* Filter and Search Bar */}
      <div className="space-y-4 bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch justify-between">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by machine name, capacity (e.g. 500L, Pasteurizer, ATM)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs sm:text-sm pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500 text-slate-800 placeholder-slate-400"
            />
          </div>

          {/* Sort selector */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-xs px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-sky-500 cursor-pointer"
            >
              <option value="featured">Featured Order</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Category Tabs (Segmented controls) */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-1 no-scrollbar">
          {categories.map((cat) => {
            const active = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap shrink-0 transition-colors cursor-pointer ${
                  active
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="py-20 text-center space-y-3 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-600 font-semibold text-sm">No machines match your criteria.</p>
          <button
            onClick={() => {
              setSelectedCategory('all');
              setSearchQuery('');
            }}
            className="text-xs text-sky-600 hover:text-sky-700 underline font-medium"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const added = isInQuote(product.id);
            return (
              <div
                key={product.id}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between group"
              >
                <div>
                  {/* Photo Container */}
                  <div className="relative aspect-4/3 bg-slate-100 overflow-hidden">
                    <SafeImage
                      src={product.image}
                      fallbackCategory={product.category}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-xs px-2.5 py-1 rounded-md text-[11px] font-bold text-slate-800 shadow-xs">
                      {product.capacity}
                    </div>
                    {product.inStock && (
                      <div className="absolute top-3 right-3 bg-emerald-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                        In Stock
                      </div>
                    )}
                  </div>

                  {/* Body Content */}
                  <div className="p-5 space-y-2.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                      {product.category.replace('-', ' ')}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 line-clamp-2 leading-snug group-hover:text-sky-700 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                      {product.tagline}
                    </p>

                    <div className="pt-2 flex items-baseline gap-2">
                      <span className="text-xl font-extrabold text-slate-900 tabular-nums">
                        KSh {formatKsh(product.priceKsh)}
                      </span>
                      {product.originalPriceKsh && (
                        <span className="text-xs text-slate-400 line-through tabular-nums">
                          KSh {formatKsh(product.originalPriceKsh)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="p-5 pt-0 border-t border-slate-100 flex items-center gap-2 mt-2">
                  <a
                    href={`/product/${product.id}`}
                    onClick={(e) => { e.preventDefault(); onNavigate('product-detail', product.id); }}
                    className="flex-1 py-2 px-3 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer text-center flex items-center justify-center gap-1.5"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>View Machine Page</span>
                  </a>

                  <button
                    onClick={() => addToQuote(product, 1)}
                    className={`py-2 px-3 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer ${
                      added
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-sky-600 text-white hover:bg-sky-700'
                    }`}
                  >
                    {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
                    <span>{added ? 'Added' : 'Add Quote'}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};
